import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AiOutlineArrowLeft } from "react-icons/ai";
import moment from "moment";
import { UAParser } from "ua-parser-js";
import { logsViewApi, getSignedUrlApi } from "../../redux/apis/logsApis";
import "./systemLogs.css";
import FilePreviewModal from "../../components/modals/documets/filePreviewModal";
import errorToast from "../../components/toasts/errorToast";
import { availableLanguages } from "../../components/data/languageData";
import Loading from "../../components/loaders/loading";


/* ── Helpers ─────────────────────────────────────────────────────────── */

const CATEGORY_STYLES = {
    AUTH: { backgroundColor: "#cfe2ff", color: "#084298" },
    USER: { backgroundColor: "#cff4fc", color: "#055160" },
    TEAM: { backgroundColor: "#e0f0ff", color: "#0c4a6e" },
    DOCUMENT: { backgroundColor: "#dbeafe", color: "#1e3a5f" },
    TRANSLATION: { backgroundColor: "#d1e7dd", color: "#0a3622" },
    RESOURCE: { backgroundColor: "#d2f4ea", color: "#13795b" },
    COMPANY: { backgroundColor: "#ffe5d0", color: "#6d2a05" },
    PUBSUB: { backgroundColor: "#f7d6e6", color: "#6a1a39" },
    CRON: { backgroundColor: "#fff3cd", color: "#664d03" },
};


const STATUS_CLASS = {
    SUCCESS: "badge bg-success",
    FAILED: "badge bg-danger",
    PENDING: "badge bg-warning text-dark",
};

const formatAction = (str) =>
    str?.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ") || "—";

const CategoryBadge = ({ category }) => {
    const style = CATEGORY_STYLES[category] || { backgroundColor: "#e2e3e5", color: "#41464b" };
    return <span className="category-badge" style={style}>{category}</span>;
};

const StatusBadge = ({ status }) => (
    <span className={STATUS_CLASS[status] || "badge bg-secondary"}>{status}</span>
);

/* Renders an enriched context card from an arbitrary object */
const EnrichedCard = ({ title, icon, data }) => {
    if (!data) return null;
    const entries = Object.entries(data).filter(([, v]) => v !== null && v !== undefined);
    if (entries.length === 0) return null;

    const formatValue = (key, val) => {
        if (typeof val === "boolean") return val ? "Yes" : "No";
        if (key === "createdAt" || key === "updatedAt" || key.endsWith("_at"))
            return moment(val).format("MMM D, YYYY h:mm A");
        if (Array.isArray(val)) return val.join(", ");
        return String(val);
    };

    const toLabel = (key) =>
        key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

    return (
        <div className="card h-100">
            <div className="card-header py-2 d-flex align-items-center gap-2 bg-light">
                <i className={`bi ${icon} text-secondary`} />
                <span className="fw_600 fs_14">{title}</span>
            </div>
            <div className="card-body p-0">
                <table className="table table-sm mb-0">
                    <tbody>
                        {entries.map(([key, val]) => (
                            <tr key={key}>
                                <td className="fw_600 fs_13 text-secondary ps-3 text-nowrap" style={{ width: 130 }}>
                                    {toLabel(key)}
                                </td>
                                <td className="fs_13 pe-3 text-break">{formatValue(key, val)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const renderMetadata = (metadata) => {
    if (!metadata) return <p className="text-muted fs_13 mb-0">No metadata</p>;

    // if (metadata.changes && Object.keys(metadata.changes).length > 0) {
    //     return (
    //         <table className="table table-sm table-bordered mb-0 text-start" style={{ maxWidth: 640 }}>
    //             <thead className="table-light">
    //                 <tr>
    //                     <th className="fs_13">Field</th>
    //                     <th className="fs_13">From</th>
    //                     <th className="fs_13">To</th>
    //                 </tr>
    //             </thead>
    //             <tbody>
    //                 {Object.entries(metadata.changes).map(([field, val]) => (
    //                     <tr key={field}>
    //                         <td><code className="fs_12">{field}</code></td>
    //                         <td><code className="fs_12">{val?.from !== undefined ? JSON.stringify(val.from) : "—"}</code></td>
    //                         <td><code className="fs_12">{val?.to !== undefined ? JSON.stringify(val.to) : "—"}</code></td>
    //                     </tr>
    //                 ))}
    //             </tbody>
    //         </table>
    //     );
    // }

    return (
        <pre className="bg-light p-3 rounded fs_12 mb-0 text-start" style={{ overflowY: "auto" }}>
            {JSON.stringify(metadata, null, 2)}
        </pre>
    );
};

const getLangName = (code) => {
    if (!code) return code;
    const match = availableLanguages.find((l) => l.value === code);
    return match ? match.label : code;
};

const keyLabel = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/* ── Details Tab Component ───────────────────────────────────────────── */

const DetailsTab = ({ metadata, targetType }) => {
    const [fileLoading, setFileLoading] = useState({});
    const [showPreview, setShowPreview] = useState(false);
    const [signedUrl, setSignedUrl] = useState(null);
    const [isPdfOrHtml, setIsPdfOrHtml] = useState(false);

    const openFile = async (url, isSourceFile = false) => {
        if (!url || fileLoading[url]) return;
        setFileLoading((prev) => ({ ...prev, [url]: true }));
        try {
            setIsPdfOrHtml(url.endsWith(".pdf") || url.endsWith(".html"));
            const res = await getSignedUrlApi(url);
            console.log("Signed URL response:", res.data);
            const signed = res.data?.data;
            if (isSourceFile) {
                setSignedUrl(signed);
                setShowPreview(true);
            } else {
                window.open(signed, "_blank", "noreferrer");
            }
        } catch {
            errorToast("Failed to open file. Please try again.");
        } finally {
            setFileLoading((prev) => ({ ...prev, [url]: false }));
        }
    };

    if (!metadata) return <p className="text-muted fs_13 mb-0 text-start">No details available.</p>;

    /* ── Translation Job ── */
    if (targetType === "translation_job") {
        const {
            file_name,
            source_lang,
            target_langs = [],
            no_of_pages,
            // source_url,
            glossary_id,
            file_url,
            job_ids = [],
            translated_data,
            metadata: inner,
        } = metadata;

        const items = (translated_data?.length ? translated_data : inner?.translated_data) || [];

        const FileBtn = ({ url, label, variant = "outline-secondary", icon = "bi-file-earmark-arrow-down", isSourceFile = false }) =>
            url ? (
                <button
                    className={`btn btn-sm btn-${variant} d-flex align-items-center gap-2`}
                    onClick={() => openFile(url, isSourceFile)}
                    disabled={!!fileLoading[url]}
                >
                    {fileLoading[url]
                        ? <span className="spinner-border spinner-border-sm" />
                        : <i className={`bi ${icon}`} />
                    }
                    {label}
                </button>
            ) : null;

        return (
            <div className="text-start">

                {/* ── Source File Card ── */}
                <div className="card mb-4">
                    <div className="card-header py-2 bg-light d-flex align-items-center gap-2">
                        <i className="bi bi-file-earmark-text text-secondary" />
                        <span className="fw_600 fs_14">Source File</span>
                    </div>
                    <div className="card-body">
                        <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
                            <div>
                                <p className="fw_600 fs_15 mb-1">{file_name || "—"}</p>
                                <div className="d-flex align-items-center gap-2 fs_13 mb-2">
                                    <span className="badge bg-light text-dark border">
                                        {getLangName(source_lang) || source_lang}
                                    </span>
                                    <i className="bi bi-arrow-right text-muted" />
                                    {Array.isArray(target_langs) ? target_langs?.map((l) => (
                                        <span key={l} className="badge bg-light text-dark border">
                                            {getLangName(l) || l}
                                        </span>
                                    )) : getLangName(target_langs) || target_langs}
                                </div>
                                <div className="d-flex flex-wrap gap-3 fs_13 text-muted">
                                    {no_of_pages && (
                                        <span><i className="bi bi-files me-1" />{no_of_pages} {no_of_pages === 1 ? "page" : "pages"}</span>
                                    )}
                                    {job_ids?.length > 0 && (
                                        <span><i className="bi bi-hash me-1" />Job ID: {job_ids[0]?.slice(0, 8)}…</span>
                                    )}
                                </div>
                            </div>
                            <FileBtn url={file_url} label="View Source" icon="bi-file-earmark-arrow-down" isSourceFile={true} />
                        </div>
                    </div>
                </div>

                {/* ── Translated Outputs ── */}
                {items.length > 0 && (
                    <>
                        <p className="fs_12 fw_600 text-muted text-uppercase mb-2" style={{ letterSpacing: "0.05em" }}>
                            Translated Outputs ({items.length})
                        </p>
                        <div className="d-flex flex-column gap-2 mb-4">
                            {items.map((item, i) => (
                                <div key={i} className="card">
                                    <div className="card-header py-2 bg-light d-flex align-items-center gap-2">
                                        <i className="bi bi-translate text-primary" />
                                        <span className="fw_600 fs_14">
                                            {getLangName(item.translatedLanguage) || item.translatedLanguage}
                                        </span>
                                        <span className="badge bg-primary-subtle text-primary-emphasis fs_12 ms-1">
                                            {item.translatedLanguage}
                                        </span>
                                        {item.is_glossary && (
                                            <span className="badge bg-success-subtle text-success-emphasis ms-1">
                                                <i className="bi bi-book me-1" />Glossary
                                            </span>
                                        )}
                                    </div>
                                    <div className="card-body p-0">
                                        <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
                                            <span className="fw_600 fs_13 text-secondary">Machine Translated Version</span>
                                            <FileBtn url={item.translatedDocumentUrl} label="Download" variant="outline-primary" icon="bi-download" />
                                        </div>
                                        {item.is_glossary && (
                                            <div className="d-flex align-items-center justify-content-between px-3 py-2">
                                                <span className="fw_600 fs_13 text-secondary">Glossary Applied Version</span>
                                                <FileBtn url={item.glossaryFileUrl} label="Download" variant="outline-secondary" icon="bi-download" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {/* ── Glossary (separate section) ── */}
                {items.some((item) => item.is_glossary) && (
                    <>
                        <p className="fs_12 fw_600 text-muted text-uppercase mb-2" style={{ letterSpacing: "0.05em" }}>
                            Glossary
                        </p>
                        <div className="d-flex flex-column gap-2">
                            {items.filter((item) => item.is_glossary).map((item, i) => (
                                <div key={i} className="card">
                                    <div className="card-header py-2 bg-light d-flex align-items-center gap-2">
                                        <i className="bi bi-book text-secondary" />
                                        <span className="fw_600 fs_14">{item.glossary_id || glossary_id}</span>
                                        <span className="badge bg-success-subtle text-success-emphasis ms-auto">
                                            <i className="bi bi-check-circle-fill me-1" />Applied
                                        </span>
                                    </div>
                                    <div className="card-body p-0">
                                        <table className="table table-sm mb-0">
                                            <tbody>
                                                <tr>
                                                    <td className="fw_600 fs_13 text-secondary ps-3 text-nowrap" style={{ width: 160 }}>Glossary ID</td>
                                                    <td className="fs_13 pe-3">{item.glossary_id || glossary_id || "—"}</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw_600 fs_13 text-secondary ps-3 text-nowrap">Applied For</td>
                                                    <td className="fs_13 pe-3">
                                                        {getLangName(item.translatedLanguage) || item.translatedLanguage}
                                                        <span className="text-muted ms-1">({item.translatedLanguage})</span>
                                                    </td>
                                                </tr>
                                                {item.resource_id && (
                                                    <tr>
                                                        <td className="fw_600 fs_13 text-secondary ps-3 text-nowrap">Resource ID</td>
                                                        <td className="fs_13 pe-3"><code className="fs_12">{item.resource_id}</code></td>
                                                    </tr>
                                                )}
                                                <tr>
                                                    <td className="fw_600 fs_13 text-secondary ps-3 text-nowrap">Glossary CSV</td>
                                                    <td className="fs_13 pe-3">
                                                        <FileBtn url={item.glossFile} label="Download CSV" icon="bi-filetype-csv" />
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
                <FilePreviewModal
                    show={showPreview}
                    onHide={() => setShowPreview(false)}
                    signedUrl={signedUrl}
                    title="Document Preview"
                    isPdfOrHtml={isPdfOrHtml}
                />
            </div>

        );
    }

    if (metadata.changes && Object.keys(metadata.changes).length > 0) {
        return (
            <table className="table table-sm table-bordered mb-0 text-start" style={{ maxWidth: 640 }}>
                <thead className="table-light">
                    <tr>
                        <th className="fs_13">Field</th>
                        <th className="fs_13">From</th>
                        <th className="fs_13">To</th>
                    </tr>
                </thead>
                <tbody>
                    {Object.entries(metadata.changes).map(([field, val]) => (
                        <tr key={field}>
                            <td><code className="fs_12">{field}</code></td>
                            <td><code className="fs_12">{val?.from !== undefined ? JSON.stringify(val.from) : "—"}</code></td>
                            <td><code className="fs_12">{val?.to !== undefined ? JSON.stringify(val.to) : "—"}</code></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    /* ── Generic metadata for all other target types ── */
    const entries = Object.entries(metadata).filter(([, v]) => v !== null && v !== undefined && typeof v !== "object");
    if (entries.length === 0)
        return <p className="text-muted fs_13 mb-0 text-start">No details available.</p>;

    return (
        <div className="row g-3 text-start">
            {entries.map(([key, val]) => {
                const display = Array.isArray(val)
                    ? val.map((v) => getLangName(v) || v).join(", ") || "—"
                    : typeof val === "boolean"
                        ? (val ? "Yes" : "No")
                        : String(val);
                return (
                    <div key={key} className="col-12 col-sm-6 col-md-4">
                        <p className="fs_12 text-muted mb-0">{keyLabel(key)}</p>
                        <p className="fs_13 fw_500 mb-0">{display}</p>
                    </div>
                );
            })}
        </div>
    );
};

/* ── Main Component ──────────────────────────────────────────────────── */

const SystemLogView = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [log, setLog] = useState(null);
    const [enriched, setEnriched] = useState(null);
    const [error, setError] = useState(null);
    const [metaTab, setMetaTab] = useState("details");
    const [pdfLoading, setPdfLoading] = useState(false);
    const contentRef = useRef(null);

    const handleDownloadPdf = async () => {
        if (pdfLoading) return;
        setPdfLoading(true);
        const el = contentRef.current;
        const btns = el ? Array.from(el.querySelectorAll("button, a.btn")) : [];
        btns.forEach((b) => { b.dataset.pdfOldDisplay = b.style.display; b.style.display = "none"; });
        try {
            const html2pdf = (await import("html2pdf.js")).default;
            await html2pdf()
                .set({
                    margin: 10,
                    filename: `log-${id}.pdf`,
                    image: { type: "jpeg", quality: 0.98 },
                    html2canvas: { scale: 2, useCORS: true },
                    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
                    pagebreak: { mode: ["css", "legacy"] },
                })
                .from(el)
                .save();
        } catch (error) {
            console.error("PDF generation error:", error);
            errorToast("Failed to generate PDF. Please try again.");
        } finally {
            btns.forEach((b) => { b.style.display = b.dataset.pdfOldDisplay || ""; delete b.dataset.pdfOldDisplay; });
            setPdfLoading(false);
        }
    };


    useEffect(() => {
        document.title = "Log Detail - Acumen Translation Hub";
        logsViewApi(id)
            .then((res) => {
                setLog(res.data.data.log);
                setEnriched(res.data.data.enriched);
                setLoading(false);
            })
            .catch((err) => {
                const status = err.response?.status;
                if (status === 404) setError("Log not found.");
                else if (status === 403) setError("You don't have permission to view this log.");
                else setError("Failed to load log. Please try again.");
                setLoading(false);
            });
    }, [id]);

    const targetTitle = log?.target_type
        ? log.target_type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
        : "Target";

    const hasEnriched =
        enriched && (enriched.actor || enriched.company || enriched.portal || enriched.target);

    return (
        <div className="w-100 d-flex flex-column" style={{ height: "100%" }}>

            {/* ── Header ───────────────────────────────────────────────── */}
            <div
                className="d-flex align-items-center border-bottom px-3 position-sticky top-0 bg-white"
                style={{ height: 53, flexShrink: 0 }}
            >
                <AiOutlineArrowLeft
                    className="cursor_pointer me-2"
                    style={{ color: "#4A81E0", fontSize: 20 }}
                    onClick={() => navigate("/logs")}
                />
                <span className="fs_18 fw_500">Log Detail</span>
                {log && <span className="ms-3"><CategoryBadge category={log.event_category} /></span>}
                {log && (
                    <button
                        className="btn btn-sm btn-outline-secondary ms-auto d-flex align-items-center gap-2"
                        onClick={handleDownloadPdf}
                        disabled={pdfLoading}
                    >
                        {pdfLoading
                            ? <><span className="spinner-border spinner-border-sm" />Generating...</>
                            : <><i className="bi bi-file-earmark-pdf" />Download PDF</>
                        }
                    </button>
                )}
            </div>

            {/* ── Content ──────────────────────────────────────────────── */}
            <div ref={contentRef} style={{ overflowY: "auto", flex: 1 }}>
                {loading ? (
                    <Loading text="Loading log..." style={{ height: "10rem" }} />
                ) : error ? (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5">
                        <i className="bi bi-exclamation-circle text-danger fs_40 mb-3" />
                        <p className="text-muted fs_14 mb-3">{error}</p>
                        <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => navigate("/system-logs")}
                        >
                            ← Back to Activity Logs
                        </button>
                    </div>
                ) : (
                    <div className="p-4 text-start" style={{ maxWidth: 900 }}>

                        {/* ── Log Details ──────────────────────────────── */}
                        <h6 className="fw_600 fs_15 mb-3 text-start">Log Details</h6>
                        <div className="card mb-4">
                            <div className="card-body p-0">
                                <table className="table table-sm mb-0 log-detail-table">
                                    <tbody>
                                        <tr>
                                            <th>ID</th>
                                            <td><code className="fs_12">{log.id}</code></td>
                                        </tr>
                                        <tr>
                                            <th>Local Timestamp</th>
                                            <td>{moment(log.createdAt).format("MMM D, YYYY h:mm:ss A")}</td>
                                        </tr>
                                        <tr>
                                            <th>User Timestamp</th>
                                            <td>
                                                {(() => {
                                                    const tzDisplay = log.timezone_display;
                                                    const tz = tzDisplay?.iana || log.timezone;
                                                    try {
                                                        const formatted = new Intl.DateTimeFormat("en-US", {
                                                            timeZone: tz || undefined,
                                                            year: "numeric", month: "short", day: "numeric",
                                                            hour: "numeric", minute: "2-digit", second: "2-digit",
                                                            hour12: true,
                                                        }).format(new Date(log.createdAt));
                                                        return (
                                                            <span>
                                                                {formatted}
                                                                {tzDisplay?.abbr && <span className="ms-2 badge bg-light text-dark border fs_12">{tzDisplay.abbr}</span>}
                                                            </span>
                                                        );
                                                    } catch {
                                                        return moment(log.createdAt).format("MMM D, YYYY h:mm:ss A");
                                                    }
                                                })()}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Timezone</th>
                                            <td>
                                                {log.timezone_display ? (
                                                    <div className="d-flex flex-column gap-1">
                                                        <span className="fs_13">{log.timezone_display.label}</span>
                                                        <div className="d-flex flex-wrap gap-1">
                                                            <span className="badge bg-light text-dark border fs_12">{log.timezone_display.abbr}</span>
                                                            <span className="badge bg-light text-dark border fs_12">{log.timezone_display.utc_offset}</span>
                                                            <code className="badge bg-light text-dark border fs_12">{log.timezone_display.iana}</code>
                                                        </div>
                                                    </div>
                                                ) : (log.timezone || "—")}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Category</th>
                                            <td><CategoryBadge category={log.event_category} /></td>
                                        </tr>
                                        <tr>
                                            <th>Action</th>
                                            <td>{formatAction(log.event_action)}</td>
                                        </tr>
                                        <tr>
                                            <th>Status</th>
                                            <td><StatusBadge status={log.event_status} /></td>
                                        </tr>
                                        <tr>
                                            <th>User ID</th>
                                            <td><code className="fs_12">{log.actor_id || "—"}</code></td>
                                        </tr>
                                        <tr>
                                            <th>User Email</th>
                                            <td>{log.actor_email || "—"}</td>
                                        </tr>
                                        <tr>
                                            <th>User Type</th>
                                            <td>{log.actor_type || "—"}</td>
                                        </tr>
                                        <tr>
                                            <th>Target Type</th>
                                            <td>{log.target_type || "—"}</td>
                                        </tr>
                                        <tr>
                                            <th>Target ID</th>
                                            <td><code className="fs_12">{log.target_id || "—"}</code></td>
                                        </tr>
                                        <tr>
                                            <th>IP Address</th>
                                            <td>
                                                {log.ip_address ? (() => {
                                                    const isV6 = log.ip_address.includes(":");
                                                    return (
                                                        <span className="d-flex align-items-center gap-2">
                                                            <code className="fs_12">{log.ip_address}</code>
                                                            <span className={`badge fs_11 ${isV6 ? "bg-info text-dark" : "bg-secondary"}`}>
                                                                {isV6 ? "IPv6" : "IPv4"}
                                                            </span>
                                                        </span>
                                                    );
                                                })() : "—"}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Device & Browser</th>
                                            <td>
                                                {log.user_agent ? (() => {
                                                    const p = new UAParser(log.user_agent).getResult();
                                                    const browser = [p.browser?.name, p.browser?.version].filter(Boolean).join(" ");
                                                    const os = [p.os?.name, p.os?.version].filter(Boolean).join(" ");
                                                    const device = p.device?.type || "Desktop";
                                                    return (
                                                        <div className="d-flex flex-column gap-1">
                                                            <div className="d-flex flex-wrap gap-2">
                                                                {browser && (
                                                                    <span className="badge bg-light text-dark border fs_12">
                                                                        <i className="bi bi-globe me-1" />{browser}
                                                                    </span>
                                                                )}
                                                                {os && (
                                                                    <span className="badge bg-light text-dark border fs_12">
                                                                        <i className="bi bi-display me-1" />{os}
                                                                    </span>
                                                                )}
                                                                <span className="badge bg-light text-dark border fs_12 text-capitalize">
                                                                    <i className="bi bi-device-hdd me-1" />{device}
                                                                </span>
                                                            </div>
                                                            <small className="text-muted fs_11 text-break">{log.user_agent}</small>
                                                        </div>
                                                    );
                                                })() : "—"}
                                            </td>
                                        </tr>
                                        {log.event_status === "FAILED" && (
                                            <tr>
                                                <th className="text-danger">Error</th>
                                                <td className="text-danger">{log.error_message || "—"}</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* ── Enriched Context ─────────────────────────── */}
                        {hasEnriched && (
                            <>
                                <h6 className="fw_600 fs_15 mb-3 text-start">Context</h6>
                                <div className="row g-3 mb-4">
                                    {enriched.actor && (
                                        <div className="col-12 col-md-6">
                                            <EnrichedCard title="User" icon="bi-person-circle" data={enriched.actor} />
                                        </div>
                                    )}
                                    {enriched.company && (
                                        <div className="col-12 col-md-6">
                                            <EnrichedCard title="Company" icon="bi-building" data={enriched.company} />
                                        </div>
                                    )}
                                    {enriched.portal && (
                                        <div className="col-12 col-md-6">
                                            <EnrichedCard title="Portal" icon="bi-columns-gap" data={enriched.portal} />
                                        </div>
                                    )}
                                    {enriched.target && (
                                        <div className="col-12 col-md-6">
                                            <EnrichedCard title={targetTitle} icon="bi-bullseye" data={enriched.target} />
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        {/* ── Metadata / Details tabs ──────────────────── */}
                        <div className="card">
                            <div className="card-header py-2 px-3 bg-white border-bottom d-flex align-items-center gap-2">
                                <button
                                    className={`btn btn-sm px-3 ${metaTab === "details" ? "btn-primary" : "btn-outline-secondary"}`}
                                    onClick={() => setMetaTab("details")}
                                >
                                    Details
                                </button>
                                <button
                                    className={`btn btn-sm px-3 ${metaTab === "metadata" ? "btn-primary" : "btn-outline-secondary"}`}
                                    onClick={() => setMetaTab("metadata")}
                                >
                                    Metadata
                                </button>
                            </div>
                            <div className="card-body">
                                {metaTab === "details"
                                    ? <DetailsTab metadata={log.metadata} targetType={log.target_type} />
                                    : renderMetadata(log.metadata)
                                }
                            </div>
                        </div>

                    </div>
                )}
            </div>


        </div>
    );
};

export default SystemLogView;
